import { useEffect } from 'react';

export const SITE_NAME = 'Hari Krishna Industries';
const CANONICAL_ORIGIN = 'https://www.hkinds.com';
const DEFAULT_TITLE =
  'Instrumentation Fittings, Compression Tube Fittings & SS316 Fittings | Hari Krishna Industries';
const DEFAULT_DESCRIPTION =
  'Hari Krishna Industries manufactures instrumentation fittings, compression tube fittings, SS316 fittings, manifolds, valves, gauges, and industrial manufacturing solutions for process industries.';
const DEFAULT_KEYWORDS =
  'instrumentation fittings, compression tube fittings, SS316 fittings, industrial manufacturing, manifolds, valves, thermowells, pressure gauges, Hari Krishna Industries';
const DEFAULT_ROBOTS =
  'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1';
const DEFAULT_IMAGE_PATH = '/hki.png';
const DEFAULT_IMAGE_ALT = 'Hari Krishna Industries logo';
const DEFAULT_THEME_COLOR = '#00a0e3';
const STRUCTURED_DATA_ID = 'seo-structured-data';
const PUBLIC_PATH = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');

const cleanUrl = (value = '') => value.replace(/([^:]\/)\/+/g, '$1');

const toSentence = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();

const trimText = (value = '', maxLength = 170) => {
  const normalized = toSentence(value);

  if (!normalized) {
    return '';
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}...`;
};

const normalizeTitle = (value) => {
  const title = toSentence(value);

  if (!title) {
    return DEFAULT_TITLE;
  }

  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
};

export const buildAbsoluteUrl = (path = '/') => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = String(path || '/').startsWith('/') ? String(path || '/') : `/${path}`;
  const origin = CANONICAL_ORIGIN;

  return cleanUrl(`${origin}${PUBLIC_PATH}${normalizedPath}`);
};

const getDefaultImageUrl = () => buildAbsoluteUrl(DEFAULT_IMAGE_PATH);

const upsertMetaTag = (attribute, key, content) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const upsertLinkTag = (rel, href) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.querySelector(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const upsertStructuredData = (graph) => {
  if (typeof document === 'undefined') {
    return;
  }

  let element = document.getElementById(STRUCTURED_DATA_ID);

  if (!element) {
    element = document.createElement('script');
    element.id = STRUCTURED_DATA_ID;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': graph,
  });
};

const createBreadcrumbStructuredData = (canonicalUrl, breadcrumbs = []) => {
  if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
    return [];
  }

  return [
    {
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumb`,
      itemListElement: breadcrumbs.map((breadcrumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: breadcrumb.name,
        item: buildAbsoluteUrl(breadcrumb.path || '/'),
      })),
    },
  ];
};

const createBaseStructuredData = ({ title, description, canonicalUrl, imageUrl }) => {
  const siteUrl = buildAbsoluteUrl('/');
  const organizationId = `${siteUrl}#organization`;
  const localBusinessId = `${siteUrl}#localbusiness`;
  const websiteId = `${siteUrl}#website`;

  return [
    {
      '@type': 'Organization',
      '@id': organizationId,
      name: SITE_NAME,
      url: siteUrl,
      logo: imageUrl,
      image: imageUrl,
      email: 'sales@hkinds.com',
      telephone: '+91 9167633933',
      foundingDate: '1995',
      sameAs: [
        'https://www.linkedin.com/company/hari-krishnaa-industries-hki/?viewAsMember=true',
        'https://www.instagram.com/harikrishnaaindustries?igsh=MXAydzl2Y2V2aWNlcA==',
      ],
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'A-1001, Viceroy Park, Opp RTO office, Kandarpada, Dahisar-West',
        addressLocality: 'Mumbai',
        postalCode: '400068',
        addressCountry: 'IN',
      },
      contactPoint: [
        {
          '@type': 'ContactPoint',
          contactType: 'sales',
          email: 'sales@hkinds.com',
          telephone: '+91 9167633933',
          areaServed: 'Worldwide',
          availableLanguage: ['English', 'Hindi'],
        },
      ],
    },
    {
      '@type': 'LocalBusiness',
      '@id': localBusinessId,
      name: SITE_NAME,
      url: siteUrl,
      image: imageUrl,
      telephone: '+91 9167633933',
      email: 'sales@hkinds.com',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'A-1001, Viceroy Park, Opp RTO office, Kandarpada, Dahisar-West',
        addressLocality: 'Mumbai',
        postalCode: '400068',
        addressCountry: 'IN',
      },
      areaServed: ['IN', 'Worldwide'],
      parentOrganization: {
        '@id': organizationId,
      },
    },
    {
      '@type': 'WebSite',
      '@id': websiteId,
      url: siteUrl,
      name: SITE_NAME,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${siteUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
      publisher: {
        '@id': organizationId,
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: title,
      description,
      inLanguage: 'en',
      isPartOf: {
        '@id': websiteId,
      },
      about: {
        '@id': organizationId,
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: imageUrl,
      },
    },
  ];
};

export const useSeo = ({
  enabled = true,
  title,
  description,
  keywords = DEFAULT_KEYWORDS,
  pathname = '/',
  image,
  imageAlt = DEFAULT_IMAGE_ALT,
  robots = DEFAULT_ROBOTS,
  type = 'website',
  structuredData = [],
  breadcrumbs = [],
} = {}) => {
  const structuredDataSignature = JSON.stringify(structuredData);
  const breadcrumbSignature = JSON.stringify(breadcrumbs);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      return;
    }

    const resolvedTitle = normalizeTitle(title);
    const resolvedDescription = trimText(description || DEFAULT_DESCRIPTION) || DEFAULT_DESCRIPTION;
    const canonicalUrl = buildAbsoluteUrl(pathname);
    const imageUrl = image ? buildAbsoluteUrl(image) : getDefaultImageUrl();
    const graph = [
      ...createBaseStructuredData({
        title: resolvedTitle,
        description: resolvedDescription,
        canonicalUrl,
        imageUrl,
      }),
      ...createBreadcrumbStructuredData(canonicalUrl, breadcrumbs),
      ...structuredData,
    ];

    document.title = resolvedTitle;

    upsertMetaTag('name', 'description', resolvedDescription);
    upsertMetaTag('name', 'keywords', keywords);
    upsertMetaTag('name', 'robots', robots);
    upsertMetaTag('name', 'googlebot', robots);
    upsertMetaTag('name', 'author', SITE_NAME);
    upsertMetaTag('name', 'theme-color', DEFAULT_THEME_COLOR);

    upsertMetaTag('property', 'og:locale', 'en_IN');
    upsertMetaTag('property', 'og:site_name', SITE_NAME);
    upsertMetaTag('property', 'og:type', type);
    upsertMetaTag('property', 'og:title', resolvedTitle);
    upsertMetaTag('property', 'og:description', resolvedDescription);
    upsertMetaTag('property', 'og:url', canonicalUrl);
    upsertMetaTag('property', 'og:image', imageUrl);
    upsertMetaTag('property', 'og:image:secure_url', imageUrl);
    upsertMetaTag('property', 'og:image:alt', imageAlt);

    upsertMetaTag('name', 'twitter:card', 'summary_large_image');
    upsertMetaTag('name', 'twitter:title', resolvedTitle);
    upsertMetaTag('name', 'twitter:description', resolvedDescription);
    upsertMetaTag('name', 'twitter:image', imageUrl);
    upsertMetaTag('name', 'twitter:image:alt', imageAlt);

    upsertLinkTag('canonical', canonicalUrl);
    upsertStructuredData(graph);
  }, [
    enabled,
    title,
    description,
    keywords,
    pathname,
    image,
    imageAlt,
    robots,
    type,
    structuredDataSignature,
    breadcrumbSignature,
  ]);
};
